import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Headline2 } from 'pages/UserProfile/Header/styled';

import { pct } from './constants';
import { IconInfo, IconLock } from './icons';
import {
  BarFill,
  BarTrack,
  UsageCard,
  UsageFraction,
  UsageFractionMuted,
  UsageGrid,
  UsageHeader,
  UsageHint,
  UsageHintSmall,
  UsageHintWarn,
  UsageLabel,
  UsageMetric,
  UsageMetricTop,
  UsageUpgradeBtn,
  UsageUpgradeNudge,
  UsageUpgradeNudgeText,
} from './styled';

type UsageMetersCardProps = {
  locked?: boolean;
  dimmed?: boolean;
  showUsageGuidance?: boolean;
  lockedHintKey?: string;
  changePlanPath: string;
  isSubscriptionOwner: boolean;
  maxMembers: number;
  maxCommunities: number;
  maxGroups: number;
  maxStorage: number;
  usedMembers: number;
  usedCommunities: number;
  usedGroups: number;
  usedStorage: number;
};

const UsageMetersCard = ({
  locked = false,
  dimmed = false,
  showUsageGuidance = false,
  lockedHintKey = 'subscriptions.profile.usage.lockedHint',
  changePlanPath,
  isSubscriptionOwner,
  maxMembers,
  maxCommunities,
  maxGroups,
  maxStorage,
  usedMembers,
  usedCommunities,
  usedGroups,
  usedStorage,
}: UsageMetersCardProps) => {
  const { t } = useTranslation();

  const m = locked ? 0 : maxMembers;
  const c = locked ? 0 : maxCommunities;
  const g = locked ? 0 : maxGroups;
  const s = locked ? 0 : maxStorage;
  const usedMembersInCard = locked ? 0 : usedMembers;
  const usedCommunitiesInCard = locked ? 0 : usedCommunities;
  const usedGroupsInCard = locked ? 0 : usedGroups;
  const usedStorageInCard = locked ? 0 : usedStorage;

  const membersAtLimit =
    showUsageGuidance && !locked && m > 0 && usedMembersInCard >= m;
  const commAtLimit =
    showUsageGuidance && !locked && c > 0 && usedCommunitiesInCard >= c;
  const schedAtLimit =
    showUsageGuidance && !locked && g > 0 && usedGroupsInCard >= g;
  const storageAtLimit =
    showUsageGuidance && !locked && s > 0 && usedStorageInCard >= s - 0.0001;
  const anyAtLimit =
    membersAtLimit || commAtLimit || schedAtLimit || storageAtLimit;

  const membersHigh =
    showUsageGuidance &&
    m > 0 &&
    pct(usedMembersInCard, m) >= 70 &&
    !membersAtLimit;

  return (
    <UsageCard>
      <UsageHeader>
        <Headline2 style={{ margin: 0 }}>
          {t('subscriptions.profile.usage.title')}
        </Headline2>
        {locked || dimmed ? (
          <UsageHint>
            <IconLock size={14} />
            {t(lockedHintKey)}
          </UsageHint>
        ) : null}
      </UsageHeader>
      <UsageGrid $dimmed={locked || dimmed}>
        <UsageMetric>
          <UsageMetricTop>
            <UsageLabel>{t('subscriptions.profile.usage.members')}</UsageLabel>
            {locked ? (
              <UsageFractionMuted>0 / -</UsageFractionMuted>
            ) : (
              <UsageFraction
                $tone={
                  membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                }
              >
                {usedMembersInCard} / {m || '-'}
              </UsageFraction>
            )}
          </UsageMetricTop>
          <BarTrack>
            {!locked && m > 0 ? (
              <BarFill
                $pct={pct(usedMembersInCard, m)}
                $tone={
                  membersAtLimit ? 'amber' : membersHigh ? 'amber' : 'default'
                }
              />
            ) : null}
          </BarTrack>
          {showUsageGuidance && !locked && m > 0 ? (
            membersAtLimit ? (
              <UsageHintWarn>
                {t('subscriptions.profile.usage.atLimitMembers')}
              </UsageHintWarn>
            ) : (
              <UsageHintSmall>
                {t('subscriptions.profile.usage.spotsRemaining', {
                  count: Math.max(0, m - usedMembersInCard),
                })}
              </UsageHintSmall>
            )
          ) : null}
        </UsageMetric>
        <UsageMetric>
          <UsageMetricTop>
            <UsageLabel>
              {t('subscriptions.profile.usage.communities')}
            </UsageLabel>
            {locked ? (
              <UsageFractionMuted>0 / -</UsageFractionMuted>
            ) : (
              <UsageFraction $tone={commAtLimit ? 'amber' : 'default'}>
                {usedCommunitiesInCard} / {c || '-'}
              </UsageFraction>
            )}
          </UsageMetricTop>
          <BarTrack>
            {!locked && c > 0 ? (
              <BarFill
                $pct={pct(usedCommunitiesInCard, c)}
                $tone={commAtLimit ? 'amber' : 'default'}
              />
            ) : null}
          </BarTrack>
          {showUsageGuidance && !locked && c > 0 ? (
            commAtLimit ? (
              <UsageHintWarn>
                {t('subscriptions.profile.usage.atLimitCommunities')}
              </UsageHintWarn>
            ) : (
              <UsageHintSmall>
                {t('subscriptions.profile.usage.communitiesRemaining', {
                  count: Math.max(0, c - usedCommunitiesInCard),
                })}
              </UsageHintSmall>
            )
          ) : null}
        </UsageMetric>
        <UsageMetric>
          <UsageMetricTop>
            <UsageLabel>
              {t('subscriptions.profile.usage.schedulers')}
            </UsageLabel>
            {locked ? (
              <UsageFractionMuted>0 / -</UsageFractionMuted>
            ) : (
              <UsageFraction $tone={schedAtLimit ? 'amber' : 'default'}>
                {usedGroupsInCard} / {g || '-'}
              </UsageFraction>
            )}
          </UsageMetricTop>
          <BarTrack>
            {!locked && g > 0 ? (
              <BarFill
                $pct={pct(usedGroupsInCard, g)}
                $tone={schedAtLimit ? 'amber' : 'default'}
              />
            ) : null}
          </BarTrack>
          {showUsageGuidance && !locked && g > 0 ? (
            schedAtLimit ? (
              <UsageHintWarn>
                {t('subscriptions.profile.usage.atLimitSchedulers')}
              </UsageHintWarn>
            ) : (
              <UsageHintSmall>
                {t('subscriptions.profile.usage.schedulersRemaining', {
                  count: Math.max(0, g - usedGroupsInCard),
                })}
              </UsageHintSmall>
            )
          ) : null}
        </UsageMetric>
        <UsageMetric>
          <UsageMetricTop>
            <UsageLabel>{t('subscriptions.profile.usage.storage')}</UsageLabel>
            {locked ? (
              <UsageFractionMuted>0.00 GB / -</UsageFractionMuted>
            ) : (
              <UsageFraction $tone={storageAtLimit ? 'amber' : 'default'}>
                {usedStorageInCard.toFixed(2)} GB / {s > 0 ? `${s} GB` : '-'}
              </UsageFraction>
            )}
          </UsageMetricTop>
          <BarTrack>
            {!locked && s > 0 ? (
              <BarFill
                $pct={pct(usedStorageInCard, s)}
                $tone={storageAtLimit ? 'amber' : 'default'}
              />
            ) : null}
          </BarTrack>
          {showUsageGuidance && !locked && s > 0 ? (
            storageAtLimit ? (
              <UsageHintWarn>
                {t('subscriptions.profile.usage.atLimitStorage')}
              </UsageHintWarn>
            ) : (
              <UsageHintSmall>
                {t('subscriptions.profile.usage.storageRemaining', {
                  count: Math.max(
                    0,
                    Math.round((s - usedStorageInCard) * 10) / 10,
                  ),
                })}
              </UsageHintSmall>
            )
          ) : null}
        </UsageMetric>
      </UsageGrid>
      {anyAtLimit ? (
        <UsageUpgradeNudge>
          <div style={{ flexShrink: 0, marginTop: 2 }}>
            <IconInfo />
          </div>
          <UsageUpgradeNudgeText>
            <strong>{t('notifications.usage_limit.title')}</strong>
            <span style={{ display: 'block', marginTop: 4 }}>
              {isSubscriptionOwner
                ? t('notifications.usage_limit.owner')
                : t('notifications.usage_limit.admin')}
            </span>
          </UsageUpgradeNudgeText>
          {isSubscriptionOwner ? (
            <UsageUpgradeBtn as={Link} to={changePlanPath}>
              {t('subscriptions.profile.usage.upgradeCta')}
            </UsageUpgradeBtn>
          ) : null}
        </UsageUpgradeNudge>
      ) : null}
    </UsageCard>
  );
};

export default UsageMetersCard;
